import { Link } from 'react-router-dom';
import { cn } from '@sglara/cn';
import CheckCircleFilled from '../../assets/icons/checkCircleFilled.svg?react';
import Warning from '../../assets/icons/warning.svg?react';
import Info from '../../assets/icons/infoIcon.svg?react';
import ChevronRight from '../../assets/icons/chevronRight.svg?react';
import { Typography } from '../Typography/Typography';

type BannerVariant = 'success' | 'warning' | 'error' | 'neutral';

const variantClasses = {
  success: 'bg-green-100 text-green-800',
  warning: 'bg-yellow-100 text-yellow-900',
  error: 'bg-red-100 text-red-800',
  neutral: 'bg-grayscale-50 text-grayscale-700',
};

const iconClasses = {
  success: 'text-green-500',
  warning: 'text-yellow-600',
  error: 'text-red-500',
  neutral: 'text-grayscale-600',
};

export type BannerProps = {
  variant: BannerVariant;
  title?: string;
  description?: string | React.ReactNode;
  url?: string;
  className?: string;
};

/**
 * A banner that appears across the top of the page.
 */
export const Banner = ({ variant, title, description, url, className }: BannerProps) => {
  const Component = url ? Link : 'div';
  const componentProps = url ? { to: url } : {};

  let icon = <Warning height="20px" width="20px" aria-hidden />;
  if (variant === 'success') {
    icon = <CheckCircleFilled height="20px" width="20px" aria-hidden />;
  } else if (variant === 'neutral') {
    icon = <Info height="20px" width="20px" aria-hidden />;
  }

  const iconClass = iconClasses[variant];
  const allClasses = cn(
    // transparent border is for windows high contrast mode
    'border-transparent border border-solid p-4 w-full box-border flex items-start justify-center gap-2 no-underline',
    variantClasses[variant],
    className,
  );

  const titleClasses = cn('flex items-center gap-2', { underline: url });

  return (
    <Component className={allClasses} {...(componentProps as any)}>
      <div className={iconClass}>{icon}</div>
      <div className="flex flex-col flex-wrap">
        {title && (
          <div className={titleClasses}>
            <Typography weight={600}>{title}</Typography>
            {url && <ChevronRight aria-hidden width="12px" height="12px" />}
          </div>
        )}
        {description && <Typography>{description}</Typography>}
      </div>
    </Component>
  );
};

export default Banner;
